import type { IWorkflowInput } from '@escloud/types';
import { Check, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface SlotDescriptor {
    key: string;
    definition: IWorkflowInput;
}

interface SlotCardProps {
    slot: SlotDescriptor;
    /** Display label of the picked dataset, or null when empty. */
    pickLabel: string | null;
    /** Position in the strip; rendered inside the numbered badge when empty. */
    index: number;
    active: boolean;
    /** Filled rows are "complete"; this controls the checkmark + green tone. */
    filled: boolean;
    onClick: () => void;
    onClear: () => void;
}

/**
 * Card in the wizard's top slot strip. One per workflow VOLUME input.
 *
 * Visual states:
 * - `empty`: numbered badge, slot name + Required/Optional pill, type
 *   chip, "Click to fill this slot" or "Pick a series from the list →"
 *   placeholder (depending on whether the card is the active focus).
 * - `filled`: green checkmark badge, slot name + Required pill, type
 *   chip, picked-series label, "Clear" affordance to release the slot
 *   without affecting other slots.
 *
 * Suitability scoring (Suitable/Marginal/Unsuitable badges from the
 * reference snapshot) is intentionally absent — the deterministic
 * rules engine is deferred to a post-Phase-3 phase. Once it lands,
 * the placeholder slot can carry the verdict badge here.
 */
export function SlotCard({ slot, pickLabel, index, active, filled, onClick, onClear }: SlotCardProps) {
    const required = slot.definition.required !== false;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                'group relative flex flex-1 min-w-0 flex-col gap-2 rounded-md border bg-card p-3 text-left transition',
                active && 'border-primary ring-2 ring-primary/20',
                !active && filled && 'border-border bg-card',
                !active && !filled && 'border-dashed border-muted-foreground/40 bg-muted/30',
                'hover:border-primary/60'
            )}
        >
            <div className="flex items-center gap-2">
                <span
                    className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        filled && 'bg-emerald-600 text-white',
                        !filled && active && 'bg-primary text-primary-foreground',
                        !filled && !active && 'bg-muted text-muted-foreground'
                    )}
                    aria-hidden
                >
                    {filled ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                <span className="truncate text-sm font-medium">{slot.definition.name}</span>
                <span
                    className={cn(
                        'text-[10px] font-medium uppercase tracking-wide',
                        required ? 'text-muted-foreground' : 'text-muted-foreground/60'
                    )}
                >
                    {required ? 'Required' : 'Optional'}
                </span>
                <div className="flex-1" />
                {filled && (
                    <span
                        role="button"
                        tabIndex={0}
                        className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                onClear();
                            }
                        }}
                        aria-label={`Clear ${slot.definition.name}`}
                    >
                        <span className="inline-flex items-center gap-0.5">
                            <X className="h-3 w-3" />
                            Clear
                        </span>
                    </span>
                )}
            </div>
            {pickLabel ? (
                <p className="truncate text-sm font-medium text-foreground">{pickLabel}</p>
            ) : (
                <p className="text-xs italic text-muted-foreground">
                    {active ? 'Pick a series from the list →' : 'Click to fill this slot'}
                </p>
            )}
        </button>
    );
}

/**
 * "Locked to <patient>" banner shown beneath the slot strip after
 * the first slot is filled. Clicking "Change patient" releases the
 * lock AND resets all picks made under it (a quietly destructive
 * action — the parent wizard handles the confirmation prompt before
 * calling `onChangePatient`).
 */
export function PatientLockBanner({
    patientLabel,
    onChangePatient,
}: {
    patientLabel: string;
    onChangePatient: () => void;
}) {
    return (
        <div className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 py-1 px-3 text-sm">
            <span className="font-medium">
                Locked to <span className="font-semibold">{patientLabel}</span> for inter-slot consistency.
            </span>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={onChangePatient}>
                Change patient
            </Button>
        </div>
    );
}

/**
 * X-of-Y indicator with per-slot color bars. Green when a slot is
 * filled, muted otherwise. Phase 3 has no verdict colors (Suitable /
 * Marginal / Not suitable) — those land with the rules-engine phase.
 */
export function SlotProgress({
    slots,
    slotPicks,
}: {
    slots: SlotDescriptor[];
    slotPicks: Record<string, string>;
}) {
    const filled = slots.filter((s) => slotPicks[s.key]).length;
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Progress:</span>
            <span
                className={cn(
                    'font-semibold',
                    filled === slots.length ? 'text-emerald-600' : 'text-foreground'
                )}
            >
                {filled} of {slots.length}
            </span>
            <span className="text-muted-foreground">slots filled</span>
            <div className="ml-2 inline-flex gap-1">
                {slots.map((s) => (
                    <span
                        key={s.key}
                        className={cn('h-1 w-5 rounded-sm', slotPicks[s.key] ? 'bg-emerald-600' : 'bg-muted')}
                    />
                ))}
            </div>
        </div>
    );
}
